import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../cn';

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** CSS grid track (e.g. '1.5fr', '24px'). */
  width: string;
  cell: (row: T) => ReactNode;
  align?: 'left' | 'right' | 'center';
}

export interface DataTableProps<T> {
  columns: readonly Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
  selectedKey?: string | null;
  /** Inner min-width so columns never crush; the wrapper scrolls horizontally. */
  minWidth?: number;
  /** Row padding: 14/16 (fleet) or 12/16 (dense). */
  dense?: boolean;
  empty?: ReactNode;
  className?: string;
  /** Hide below the 760 px breakpoint (consumer renders ListRows instead). */
  hideOnMobile?: boolean;
}

/**
 * CSS-grid table as in the design: micro header row, 1 px line between rows, hover fill, overflow-x auto with
 * a min-width inner track. Columns are `grid-template-columns` fractions.
 */
export function DataTable<T>({ columns, rows, rowKey, onRowClick, selectedKey, minWidth = 720, dense, empty, className, hideOnMobile = true }: DataTableProps<T>) {
  const grid: CSSProperties = { gridTemplateColumns: columns.map((c) => c.width).join(' ') };
  return (
    <div className={cn('overflow-x-auto', hideOnMobile && 'hidden md:block', className)}>
      <div style={{ minWidth }}>
        <div className="micro grid items-center gap-3 px-4 py-3 text-fg4" style={grid}>
          {columns.map((c) => (
            <span key={c.key} className={cn(c.align === 'right' && 'text-right', c.align === 'center' && 'text-center')}>
              {c.header}
            </span>
          ))}
        </div>
        {rows.length === 0 && empty}
        {rows.map((r) => {
          const k = rowKey(r);
          const selected = selectedKey === k;
          return (
            <div
              key={k}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={() => onRowClick?.(r)}
              onKeyDown={(e) => e.key === 'Enter' && onRowClick?.(r)}
              className={cn('group grid items-center gap-3 border-t border-line px-4', dense ? 'py-3' : 'py-3.5', onRowClick && 'cursor-pointer hover:bg-hover', selected && 'bg-selected')}
              style={grid}
            >
              {columns.map((c) => (
                <div key={c.key} className={cn('min-w-0', c.align === 'right' && 'text-right', c.align === 'center' && 'text-center')}>
                  {c.cell(r)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Two-line table cell: 500 13px title + mono 10px fg4 sub. */
export function CellTitle({ title, sub, className }: { title: ReactNode; sub?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex min-w-0 flex-col gap-[5px]', className)}>
      <span className="truncate text-md font-medium leading-none">{title}</span>
      {sub && <span className="font-mono text-xs font-normal tracking-[.04em] text-fg4">{sub}</span>}
    </div>
  );
}
/** Mono data cell: 400 11px fg3 (tabular optional). */
export function CellMono({ children, tabular, className }: { children: ReactNode; tabular?: boolean; className?: string }) {
  return <span className={cn('font-mono text-sm font-normal text-fg3', tabular && 'tabular', className)}>{children}</span>;
}
/** Body text cell: 400 13px/1.3 fg2. */
export function CellText({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cn('text-md font-normal leading-[1.3] text-fg2', className)}>{children}</span>;
}
